import {
  type Classroom,
  nextStudentNumber,
  normalizeRosterClass,
  type RosterRow,
  type Student,
  studentNumberLabel,
  validateNewStudent,
} from "@classroom/domain";
import { useRef, useState } from "react";
import { useTranslation } from "./i18n";
import "./roster-administration.css";

export default function NewStudentForm({
  classItem,
  students,
  readOnly,
  onAdd,
  onCreated,
  toast,
}: {
  classItem?: Classroom;
  students: Student[];
  readOnly: boolean;
  onAdd: (row: RosterRow) => Promise<Student>;
  onCreated: (student: Student) => void;
  toast: (message: string) => void;
}) {
  const { t } = useTranslation("rosterAdmin");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const crossClass = students.some((student) => student.sourceClass);

  async function add(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending.current || readOnly || !classItem) return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    const number = Number(fields.get("number"));
    try {
      setError("");
      const row = validateNewStudent(
        {
          number,
          name: String(fields.get("name") || ""),
          gender: String(fields.get("gender") || ""),
          ...(crossClass ? { sourceClass: normalizeRosterClass(String(fields.get("sourceClass") ?? "")) } : {}),
        },
        students,
      );
      pending.current = true;
      setBusy(true);
      const student = await onAdd(row);
      form.reset();
      const numberField = form.elements.namedItem("number") as HTMLInputElement;
      numberField.value = String(nextStudentNumber([...students, student]) ?? "");
      setDirty(false);
      onCreated(student);
      toast(t("added", { number: studentNumberLabel(student), name: student.name }));
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      switch (message) {
        case "duplicate-student-number":
          setError(t("duplicateNumber", { number }));
          break;
        case "invalid-source-class":
          setError(t("invalidSourceClass"));
          break;
        case "invalid-student-number":
          setError(t("invalidNumber"));
          break;
        case "missing-student-name":
          setError(t("missingName"));
          break;
        case "invalid-student-gender":
          setError(t("invalidGender"));
          break;
        case "invalid-roster-class":
          setError(t("invalidClass"));
          break;
        default:
          setError(message || t("saveFailed"));
      }
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  return (
    <details className="paper-card roster-student-add">
      <summary aria-label={t("addStudentEntry")}>
        {t("addStudent")} <small>{t("addStudentHint")}</small>
      </summary>
      <form
        className="compact-form student-add-card"
        onSubmit={add}
        onChange={() => setDirty(true)}
        data-save-state={busy ? "saving" : dirty ? "dirty" : "saved"}
      >
        <span className="student-add-class">{classItem?.name || t("chooseClass")}</span>
        <fieldset disabled={readOnly || busy || !classItem}>
          {crossClass && (
            <label htmlFor="new-student-source-class">
              {t("sourceClass")}
              <input id="new-student-source-class" name="sourceClass" required maxLength={40} autoComplete="off" />
            </label>
          )}
          <label htmlFor="new-student-number">
            {t("number")}
            <input
              id="new-student-number"
              aria-label={t("numberLabel")}
              name="number"
              type="number"
              min="1"
              max="999"
              step="1"
              required
              defaultValue={nextStudentNumber(students) ?? ""}
            />
          </label>
          <label htmlFor="new-student-name">
            {t("name")}
            <input id="new-student-name" aria-label={t("nameLabel")} name="name" required autoComplete="off" />
          </label>
          <label htmlFor="new-student-gender">
            {t("gender")}
            <select id="new-student-gender" aria-label={t("genderLabel")} name="gender" defaultValue="">
              <option value="">{t("unspecified")}</option>
              <option value="M">{t("male")}</option>
              <option value="F">{t("female")}</option>
              <option value="X">{t("other")}</option>
            </select>
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="primary-action">
            {t(busy ? "adding" : "addStudent")}
          </button>
        </fieldset>
      </form>
    </details>
  );
}
